import { useEffect, useRef, useState } from "react";

interface Props {
  onDone: () => void;
}

// Splash gating
// - Long-term: localStorage flag (per asset version). After it's set, returning
//   visitors get a static first-frame flash instead of the full video.
// - Per-tab: sessionStorage flag flipped on mount. Even if something causes
//   a remount within the same tab (SW activation, hot reload), splash plays
//   at most once per tab session.
//
// The "_v2_apr27" suffix on the long-term key is bumped each time the video
// asset is replaced so returning users see the new video once.
const VIDEO_SHOWN_KEY = "nigunim:splashVideoShown_v2_apr27";
const SESSION_SEEN_KEY = "nigunim:splashSeenThisSession";

type Mode = "video" | "static";

function chooseMode(): Mode {
  return localStorage.getItem(VIDEO_SHOWN_KEY) === "true" ? "static" : "video";
}

/** App-level guard: don't show the splash at all if it already played in
 *  this tab session. Keeps remounts (SW activation etc.) from re-triggering. */
export function needsSplash(): boolean {
  if (typeof sessionStorage === "undefined") return true;
  return sessionStorage.getItem(SESSION_SEEN_KEY) !== "true";
}

export function SplashVideo({ onDone }: Props) {
  const [mode] = useState<Mode>(chooseMode);
  const [canSkip, setCanSkip] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Mark this tab session as seen the moment the splash mounts. Future
  // remounts in the same tab will skip the splash via needsSplash().
  useEffect(() => {
    try { sessionStorage.setItem(SESSION_SEEN_KEY, "true"); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (mode === "static") {
      const showTimer = setTimeout(() => setFadingOut(true), 400);
      const doneTimer = setTimeout(() => onDone(), 700);
      return () => {
        clearTimeout(showTimer);
        clearTimeout(doneTimer);
      };
    }

    // Video mode — enable tap-to-skip after 1.5s
    const skipTimer = setTimeout(() => setCanSkip(true), 1500);
    return () => clearTimeout(skipTimer);
  }, [mode, onDone]);

  const finish = () => {
    if (fadingOut) return;
    localStorage.setItem(VIDEO_SHOWN_KEY, "true");
    setFadingOut(true);
    setTimeout(onDone, 300);
  };

  const handleTap = () => {
    if (mode === "video" && canSkip) finish();
  };

  return (
    <div
      onClick={handleTap}
      className="fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-300"
      style={{
        opacity: fadingOut ? 0 : 1,
        pointerEvents: fadingOut ? "none" : "auto",
        background: "#06122B",
      }}
    >
      {mode === "video" ? (
        <video
          ref={videoRef}
          src="/splash-mobile.mp4"
          autoPlay
          muted
          playsInline
          preload="auto"
          onEnded={finish}
          onError={finish}
          className="w-full h-full object-cover"
          style={{ background: "#06122B", mixBlendMode: "screen" }}
        />
      ) : (
        // Static mode: don't autoplay — the video would play silently behind
        // the fade-out and the user perceives it as "splash playing again".
        // Just hold the dark navy background for the same fade window.
        <div
          aria-hidden
          style={{ width: "100%", height: "100%", background: "#06122B" }}
        />
      )}
    </div>
  );
}
